import { logger } from './logger'

export interface ToolContext {
  supabase?: any
  userId?: string
  conversations?: { id: string; title: string; mode?: string; updatedAt: number }[]
}

const QURAN_API = 'https://api.alquran.cloud/v1'
const SURAH_CACHE_KEY = 'islam-preacher-surahs'

async function listSurahs(): Promise<string> {
  const cached = localStorage.getItem(SURAH_CACHE_KEY)
  if (cached) {
    return JSON.stringify({ results: JSON.parse(cached) })
  }
  try {
    const resp = await fetch(`${QURAN_API}/surah`)
    const data = await resp.json()
    if (data.code === 200) {
      localStorage.setItem(SURAH_CACHE_KEY, JSON.stringify(data.data))
      return JSON.stringify({ results: data.data })
    }
    return JSON.stringify({ error: `API error: ${data.code}` })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Failed to fetch surahs' })
  }
}

async function getSurah(number: number): Promise<string> {
  try {
    const resp = await fetch(`${QURAN_API}/surah/${number}/editions/en.asad,en.pickthall,en.yusufali`)
    const data = await resp.json()
    if (data.code === 200) {
      const main = data.data[0]
      const ayahs = main.ayahs.map((a: any) => ({
        number: a.number,
        text: a.text,
        numberInSurah: a.numberInSurah,
        juz: a.juz,
        page: a.page,
        ruku: a.ruku,
        sajda: a.sajda,
      }))
      return JSON.stringify({
        surah: {
          number: main.number,
          name: main.name,
          englishName: main.englishName,
          englishNameTranslation: main.englishNameTranslation,
          revelationType: main.revelationType,
          numberOfAyahs: main.numberOfAyahs,
          ayahs,
        },
      })
    }
    return JSON.stringify({ error: `API error: ${data.code}` })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Failed to fetch surah' })
  }
}

const VECTOR_DB_URL = 'https://riddwam-al-munazir-ai.hf.space/search'
const RAW_API_URL = 'https://riddwam-al-munazir-ai.hf.space/raw'

async function searchVectorDb(query: string, topK: number, category?: string): Promise<string> {  try {
    const body: any = { query, top_k: Math.min(Math.max(1, topK), 20) }
    if (category) body.category = category
    const resp = await fetch(VECTOR_DB_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: AbortSignal.timeout(15000),
    })
    if (!resp.ok) return JSON.stringify({ error: `Search returned ${resp.status}` })
    const data = await resp.json()
    const results = Array.isArray(data) ? data : (data.results || [])
    if (!results.length) return JSON.stringify({ results: [], message: 'No matching results found.' })
    return JSON.stringify({
      results: results.slice(0, topK).map((r: any) => ({
        text: r.text || '',
        source: r.source || r.category || 'Unknown',
        religion: r.religion || '',
        category: r.category || '',
        score: r.score ?? 0,
        title: r.title || '',
      })),
    })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Search failed' })
  }
}

export const TOOL_DEFINITIONS = [
  {
    type: 'function',
    function: {
      name: 'search_debate_transcripts',
      description: 'Search theological discussion transcripts for arguments, counter-arguments, scripture refutations, and historical evidence. Use whenever you need examples of how specific theological points were argued.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'The search query — what evidence or context you need' },
          top_k: { type: 'number', description: 'Number of results (1-10)', default: 5 },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_quran',
      description: 'Search the Quran (118,484 verse-level chunks from tanzil.net) by keyword, topic, or concept. Returns matching verses with Arabic text and context. Use when the user asks about specific Quranic teachings, wants to find verses on a topic, or needs Quranic evidence.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Keyword, topic, or concept to search for in the Quran' },
          top_k: { type: 'number', description: 'Number of results (1-20)', default: 10 },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_bible',
      description: 'Search the Bible (254,369 verse-level chunks across 10+ translations including KJV, ASV, YLT, Douay-Rheims) by keyword, book, passage reference, or topic. Use when comparing Islamic and Christian scriptures, or when the user asks about Bible content.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Keyword, passage reference, or search term' },
          top_k: { type: 'number', description: 'Number of results (1-20)', default: 5 },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_hadith',
      description: 'Search authentic hadith collections (72,396 chunks from LK-Hadith-Corpus covering Bukhari, Muslim, Abu Dawud, Tirmidhi, Nasai, Ibn Majah) by keyword. Returns hadith with text, chain, and grading. Use when the user asks about prophetic traditions, sunnah, or specific sayings of the Prophet.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Keyword or phrase to search for in hadith' },
          top_k: { type: 'number', default: 5 },
        },
        required: ['query'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'list_quran_surahs',
      description: 'List all 114 surahs of the Quran with their names, English translations, revelation type, and verse count. Use when the user asks for a list of surahs or wants to know what surahs exist.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_quran_surah',
      description: 'Get a full surah by its numeric number (1-114), including Arabic text and three English translations (Asad, Pickthall, Yusuf Ali). Use when the user wants to read an entire surah or a specific passage with full context.',
      parameters: {
        type: 'object',
        properties: {
          number: { type: 'number', description: 'Surah number (1-114)' },
        },
        required: ['number'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'lookup_quran_verse',
      description: 'Look up a specific verse in the Quran by surah number and verse number. Returns the verse text with three English translations (Asad, Pickthall, Yusuf Ali). Use when the user references a specific verse like "Quran 9:123".',
      parameters: {
        type: 'object',
        properties: {
          surah: { type: 'number', description: 'Surah number (1-114)' },
          verse: { type: 'number', description: 'Verse number within the surah' },
        },
        required: ['surah', 'verse'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_raw_text',
      description: 'Retrieve the complete raw text of a religious scripture file from the full-text dataset. Use when the user asks for an entire surah, book, or collection. To find available files, first call list_raw_files to see what is available.',
      parameters: {
        type: 'object',
        properties: {
          category: { type: 'string', enum: ['christianity', 'islam', 'judaism', 'hinduism'], description: 'Religious category' },
          file_path: { type: 'string', description: 'Relative file path within the category (e.g., "quran/translations/en_sahih/full_text.txt"). Use list_raw_files to discover available paths.' },
        },
        required: ['category', 'file_path'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'list_raw_files',
      description: 'List all available raw text file paths in a religious category. Use this to discover what full-text files are available before calling get_raw_text.',
      parameters: {
        type: 'object',
        properties: {
          category: { type: 'string', enum: ['christianity', 'islam', 'judaism', 'hinduism'], description: 'Religious category to list files for' },
        },
        required: ['category'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'get_conversation_history',
      description: 'Get the full message history of a specific previous conversation. Use when the user refers to a past discussion or you need context from an earlier chat. Returns all user and assistant messages with content.',
      parameters: {
        type: 'object',
        properties: {
          conversation_id: { type: 'string', description: 'The ID of the conversation to retrieve' },
          limit: { type: 'number', description: 'Max messages to return (default 50)', default: 50 },
        },
        required: ['conversation_id'],
      },
    },
  },
  {
    type: 'function',
    function: {
      name: 'search_conversations',
      description: 'Search the user\'s past conversations by title or content. Use when you need to find if a topic was previously discussed. Returns matching conversation titles, IDs, modes, and preview snippets.',
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: 'Search term to find in conversation titles or content' },
          limit: { type: 'number', description: 'Max results (default 10)', default: 10 },
        },
        required: ['query'],
      },
    },
  },
]

async function searchDebateTranscripts(query: string, topK: number = 5): Promise<string> {
  return searchVectorDb(query, topK, 'transcripts')
}

async function searchQuran(query: string, topK: number = 10): Promise<string> {
  return searchVectorDb(query, topK, 'quran')
}

async function searchBible(query: string, topK: number = 5): Promise<string> {
  return searchVectorDb(query, topK, 'bible')
}

async function searchHadith(query: string, topK: number = 5): Promise<string> {
  return searchVectorDb(query, topK, 'hadith')
}

async function lookupQuranVerse(args: any): Promise<string> {
  const { surah, verse } = args
  if (typeof surah !== 'number' || typeof verse !== 'number') {
    return JSON.stringify({ error: 'Both surah and verse must be numbers' })
  }
  return await (async () => {
    try {
      const resp = await fetch(`${QURAN_API}/surah/${surah}/editions/en.asad,en.pickthall,en.yusufali`)
      const data = await resp.json()
      if (data.code === 200) {
        const main = data.data[0]
        const ayah = main.ayahs.find((a: any) => a.numberInSurah === verse)
        if (!ayah) {
          return JSON.stringify({ error: `Verse ${verse} not found in Surah ${surah}` })
        }
        return JSON.stringify({
          surah: main.englishName,
          verse,
          text: ayah.text,
          translations: {
            asad: ayah.text,
            pickthall: data.data[1]?.ayahs.find((a: any) => a.number === ayah.number)?.text || '',
            yusufAli: data.data[2]?.ayahs.find((a: any) => a.number === ayah.number)?.text || '',
          },
        })
      }
      return JSON.stringify({ error: `API error: ${data.code}` })
    } catch (err) {
      return JSON.stringify({ error: err instanceof Error ? err.message : 'Failed to fetch verse' })
    }
  })()
}

async function getRawText(args: any): Promise<string> {
  const { category, file_path } = args
  if (!category || !file_path) {
    return JSON.stringify({ error: 'Both category and file_path are required' })
  }
  try {
    const encodedPath = (file_path as string).split('/').map(p => encodeURIComponent(p)).join('/')
    const resp = await fetch(`${RAW_API_URL}/${encodeURIComponent(category)}/${encodedPath}`)
    if (!resp.ok) {
      const text = await resp.text()
      return JSON.stringify({ error: `Raw fetch failed: ${resp.status} - ${text}` })
    }
    const data = await resp.json()
    return JSON.stringify({ text: data.text })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Raw fetch error' })
  }
}

async function listRawFiles(args: any): Promise<string> {
  const { category } = args
  if (!category) return JSON.stringify({ error: 'category is required' })
  try {
    const resp = await fetch(`${RAW_API_URL}/${encodeURIComponent(category)}`)
    if (!resp.ok) return JSON.stringify({ error: `List failed: ${resp.status}` })
    const data = await resp.json()
    return JSON.stringify({ category: data.category, count: data.count, files: data.files })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'List failed' })
  }
}

async function getConversationHistory(args: any, ctx?: ToolContext): Promise<string> {
  if (!ctx?.supabase || !ctx?.userId) return JSON.stringify({ error: 'Not available outside authenticated chat' })
  try {
    const { data } = await ctx.supabase
      .from('chat_messages')
      .select('role, content, thinking')
      .eq('session_id', args.conversation_id)
      .order('created_at', { ascending: true })
      .limit(args.limit || 50)
    if (!data || data.length === 0) return JSON.stringify({ error: 'Conversation not found or empty' })
    return JSON.stringify({ messages: data })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Failed to get history' })
  }
}

async function searchConversations(args: any, ctx?: ToolContext): Promise<string> {
  if (!ctx?.supabase || !ctx?.userId) return JSON.stringify({ error: 'Not available outside authenticated chat' })
  try {
    const q = args.query?.toLowerCase() || ''
    const { data } = await ctx.supabase
      .from('chat_sessions')
      .select('id, title, mode, created_at')
      .eq('user_id', ctx.userId)
      .order('created_at', { ascending: false })
      .limit(50)

    if (!data) return JSON.stringify({ results: [] })

    const filtered = data.filter((s: any) =>
      (s.title || '').toLowerCase().includes(q) ||
      (s.mode || '').toLowerCase().includes(q)
    ).slice(0, args.limit || 10)

    const results: any[] = []
    for (const session of filtered) {
      const { data: msgs } = await ctx.supabase
        .from('chat_messages')
        .select('role, content')
        .eq('session_id', session.id)
        .order('created_at', { ascending: false })
        .limit(1)
      results.push({
        id: session.id,
        title: session.title,
        mode: session.mode,
        preview: msgs?.[0]?.content?.slice(0, 100) || '',
        created_at: session.created_at,
      })
    }

    return JSON.stringify({ results })
  } catch (err) {
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Search failed' })
  }
}

const HANDLERS: Record<string, (args: any, ctx?: ToolContext) => Promise<string>> = {
  search_debate_transcripts: (args) => searchDebateTranscripts(args.query, args.top_k),
  search_quran: (args) => searchQuran(args.query, args.top_k),
  search_bible: (args) => searchBible(args.query, args.top_k),
  search_hadith: (args) => searchHadith(args.query, args.top_k),
  list_quran_surahs: () => listSurahs(),
  get_quran_surah: (args) => getSurah(args.number),
  lookup_quran_verse: (args) => lookupQuranVerse(args),
  get_raw_text: (args) => getRawText(args),
  list_raw_files: (args) => listRawFiles(args),
  get_conversation_history: (args, ctx) => getConversationHistory(args, ctx),
  search_conversations: (args, ctx) => searchConversations(args, ctx),
}

export async function executeTool(name: string, args: any, ctx?: ToolContext): Promise<string> {
  const handler = HANDLERS[name]
  if (!handler) {
    logger.warn('Unknown tool called', { toolName: name, args })
    return JSON.stringify({ error: `Unknown tool: ${name}` })
  }
  logger.debug('Executing tool', { toolName: name, args })
  try {
    const result = await handler(args, ctx)
    logger.debug('Tool execution completed', { toolName: name, resultLength: result.length })
    return result
  } catch (err) {
    logger.captureException(err, { context: 'executeTool', toolName: name, args })
    return JSON.stringify({ error: err instanceof Error ? err.message : 'Tool execution failed' })
  }
}

export const TOOL_NAMES = Object.keys(HANDLERS)

export const TOOL_LABELS: Record<string, string> = {
  search_debate_transcripts: 'Searching debate transcripts...',
  search_quran: 'Searching the Quran (118K verses)...',
  search_bible: 'Searching the Bible (254K verses)...',
  search_hadith: 'Searching hadith (72K narrations)...',
  list_quran_surahs: 'Loading Quran surah list...',
  get_quran_surah: 'Fetching surah from Quran.com API...',
  lookup_quran_verse: 'Looking up Quran verse...',
  get_raw_text: 'Fetching raw scripture file...',
  list_raw_files: 'Listing available raw text files...',
  get_conversation_history: 'Loading conversation history...',
  search_conversations: 'Searching conversations...',
}

export const TOOL_ICONS: Record<string, string> = {
  search_debate_transcripts: 'BookOpen',
  search_quran: 'BookText',
  search_bible: 'Scroll',
  search_hadith: 'Library',
  list_quran_surahs: 'List',
  get_quran_surah: 'BookOpen',
  lookup_quran_verse: 'BookMarked',
  get_raw_text: 'FileText',
  list_raw_files: 'FolderOpen',
  get_conversation_history: 'MessageSquare',
  search_conversations: 'Search',
}
